/**
 * components/Toast/showToast.ts
 * ──────────────────────────────────────────────────────────────────────────
 * The imperative toast API. Every state change in this product toasts, and
 * this is the only function that makes one.
 *
 * Screens do not call this directly — they go through utils/successHandler.ts
 * and utils/errorHandler.ts, which extract the REAL backend message first.
 * The one exception is client-side validation ("Give the rule an amount."),
 * where there is no backend response to quote.
 *
 * Duration is 4.2s for every tone, as the design specifies. It is not
 * shortened for successes: a person reading a variance figure off a toast
 * needs the same time as one reading an error.
 * ──────────────────────────────────────────────────────────────────────────
 */
import Toast from 'react-native-toast-message';
import type { ToastTone } from './types';

/** The design's dwell time — 4200ms for every tone. */
export const TOAST_DURATION = 4200;

export function showToast(tone: ToastTone, title: string, message?: string): void {
  Toast.show({
    type: tone,
    text1: title,
    text2: message,
    position: 'bottom',
    bottomOffset: 20,
    visibilityTime: TOAST_DURATION,
    autoHide: true,
  });
}

/** Shorthands, so call sites read as the thing that happened. */
export const toastSuccess = (title: string, message?: string) => showToast('success', title, message);
export const toastError = (title: string, message?: string) => showToast('danger', title, message);
export const toastWarn = (title: string, message?: string) => showToast('warn', title, message);
/** Neutral state change — the white left border. */
export const toastPlain = (title: string, message?: string) => showToast('plain', title, message);

/** Dismiss any visible toast — used when a screen unmounts mid-flow. */
export const hideToast = (): void => Toast.hide();
